import type { Brief } from '@studio/shared';

const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string) ?? 'http://localhost:8787';

/**
 * The child's device token. Issued by a parent from the console and stored per
 * device — there are no passwords for children. It authorises API calls only;
 * it grants nothing inside a game frame, which has no access to this origin.
 */
const TOKEN_KEY = 'studio.deviceToken';

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private browsing; the session simply will not persist */
  }
};

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) {
    throw new Error(response.status === 401 ? 'unauthorized' : `request_failed_${response.status}`);
  }
  return response.json() as Promise<T>;
}

export type TemplateSummary = { id: string; name: string; blurb: string };

export type Me = {
  kid: { id: string; name: string; profile: 'sprout' | 'builder' | 'engineer'; isActive: boolean };
  credits: number;
  minutesLeftThisWeek: number;
  theme: { title: string; hint: string } | null;
  templates: TemplateSummary[];
  session: { id: string; endsAt: number; msRemaining: number } | null;
};

export type BuildResult =
  | {
      ok: true;
      buildId: string;
      gameUrl: string;
      credits: number;
      coachScore: number | null;
      coachQuestion: string | null;
    }
  | { ok: false; code: string; childMessage: string };

export const getMe = () => call<Me>('/api/me');

export const build = (input: {
  brief: Brief;
  templateId: string;
  kind?: 'build' | 'tweak' | 'fix' | 'repair';
  parentBuildId?: string;
  instruction?: string;
}) => call<BuildResult>('/api/build', { method: 'POST', body: JSON.stringify(input) });

// ------------------------------------------------------------------ arcade

export type ArcadeGame = {
  id: string;
  buildId: string;
  title: string;
  ownerId: string;
  ownerName: string;
  isMine: boolean;
  remixOf: string | null;
  publishedAt: number;
  plays: number;
  averageRating: number | null;
  myRating: number | null;
};

export type RankedEntry = {
  kidId: string;
  name: string;
  points: number;
  rank: number;
  gamesShipped: number;
  playsReceived: number;
  remixesReceived: number;
  bestBriefScore: number;
};

export type Leaderboard = {
  boards: { band: string; entries: RankedEntry[] }[];
  badges: { id: string; label: string; kidId: string }[];
};

export const getArcade = () => call<{ games: ArcadeGame[] }>('/api/arcade');
export const getLeaderboard = () => call<Leaderboard>('/api/leaderboard');

export const playGame = (id: string) =>
  call<{ ok: true; counted: boolean }>(`/api/games/${id}/play`, { method: 'POST' });

export const rateGame = (id: string, rating: number) =>
  call<{ ok: true }>(`/api/games/${id}/rate`, {
    method: 'POST',
    body: JSON.stringify({ rating }),
  });

export const remixGame = (id: string) =>
  call<{ ok: true; buildId: string; gameUrl: string } | { ok: false; childMessage: string }>(
    `/api/games/${id}/remix`,
    { method: 'POST' },
  );

export const publishBuild = (buildId: string, title: string) =>
  call<{ ok: boolean; gameId?: string }>('/api/publish', {
    method: 'POST',
    body: JSON.stringify({ buildId, title }),
  });

// ------------------------------------------------------------- tweak panel

import type { Tunable } from '@studio/shared';

export const getSource = (buildId: string) =>
  call<{ html: string }>(`/api/builds/${buildId}/source`);

export const getTunables = (buildId: string) =>
  call<{ tunables: Tunable[] }>(`/api/builds/${buildId}/tunables`);

export type TweakResult =
  | { ok: true; buildId: string; gameUrl: string; tunables: Tunable[] }
  | { ok: false; code: string; childMessage?: string };

export const applyTweaks = (buildId: string, changes: { name: string; value: number }[]) =>
  call<TweakResult>(`/api/builds/${buildId}/tunables`, {
    method: 'POST',
    body: JSON.stringify({ changes }),
  });

// ---------------------------------------------------------------- pitch mode

import type { Pitch, PitchMaths } from '@studio/shared';

export type PitchState = {
  unlocked: boolean;
  gamesShipped: number;
  needed: number;
  pitches: { id: string; gameId: string; title: string; at: number; answers: unknown }[];
};

export type PitchResult =
  | { ok: true; maths: PitchMaths; verdict: string }
  | { ok: false; code: string; needed?: number };

export const getPitches = () => call<PitchState>('/api/pitches');

export const makePitch = (gameId: string, pitch: Pitch) =>
  call<PitchResult>(`/api/games/${gameId}/pitch`, {
    method: 'POST',
    body: JSON.stringify(pitch),
  });

export type StartSessionResult =
  | { ok: true; session: { id: string; endsAt: number; msRemaining: number } }
  | { ok: false; code: string; childMessage: string };

export const startSession = () =>
  call<StartSessionResult>('/api/session/start', { method: 'POST' });
